import { Texto } from '@gravitate-js/excalibrr'
import { Image } from 'antd'
import { useEffect, useState } from 'react'
import { fetchArtifact } from './artifactFetch'

export type InlineImageProps = {
  // Fetch mode: load an artifact produced by a task from the authenticated endpoint.
  taskId?: string
  filename: string
  // Direct mode: render this URL as-is (e.g. a data: URL for an inline attachment).
  // When provided, no fetch is performed.
  src?: string
}

export function InlineImage({
  taskId,
  filename,
  src: srcProp,
}: InlineImageProps) {
  const [fetchedSrc, setFetchedSrc] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)

  // Direct src wins; otherwise fall back to the fetched blob URL.
  const src = srcProp ?? fetchedSrc

  useEffect(() => {
    // Direct mode (or no task to fetch from) — nothing to load.
    if (srcProp || !taskId) return

    let revoked = false
    let createdUrl: string | null = null
    const controller = new AbortController()

    const load = async () => {
      const resp = await fetchArtifact(taskId, filename, controller.signal)
      if (resp.status === 410) {
        if (!revoked) setExpired(true)
        return
      }
      if (!resp.ok) return
      const blob = await resp.blob()
      createdUrl = URL.createObjectURL(blob)
      if (!revoked) setFetchedSrc(createdUrl)
    }
    // Fire-and-forget; an aborted fetch rejects with AbortError on unmount,
    // which is expected — swallow it.
    load().catch(() => {
      // ignore (e.g. AbortError on unmount)
    })

    return () => {
      revoked = true
      controller.abort()
      if (createdUrl) URL.revokeObjectURL(createdUrl)
    }
  }, [taskId, filename, srcProp])

  if (expired) {
    return <Texto appearance='hint'>Image expired</Texto>
  }
  if (!src) {
    return <Texto appearance='hint'>Loading image...</Texto>
  }
  // AntD Image gives a click-to-open preview overlay (zoom/close) instead of
  // navigating away — the standard in-app image preview.
  return (
    <Image
      src={src}
      alt={filename}
      wrapperStyle={{ maxWidth: '100%' }}
      style={{
        maxWidth: '100%',
        maxHeight: 320,
        height: 'auto',
        display: 'block',
        borderRadius: 4,
      }}
      preview={{ mask: 'Preview' }}
    />
  )
}
